/**
 * @file LegalDocumentsListPage.tsx
 * @description Список юридических документов магазина (phase10.5.X;
 * phase19.16 — виды «Текст / PDF / Группа», состояния и редакции,
 * SB139–SB144; uiux-legal-docs §2, uiux-legal-steps-b-c §6.4).
 *
 * Под SHOP_MANAGE_SETTINGS (маршрут, SB144). Отбор «Все / Опубликованные /
 * Черновики» и «Тип»; карточки — `LegalDocumentRow` (на ПК в две колонки).
 * «+ Новый документ» открывает выбор вида (`LegalKindPicker`); четвёртая
 * карточка создаёт автоматическую группу сертификатов и открывает её.
 * После мутаций список перечитывается, а не правится руками.
 */

import { LEGAL_DOCUMENT_KIND_LABELS, LEGAL_DOCUMENT_KINDS, type LegalDocumentKind, type LegalListStatusFilter } from "@chaiberi/shared";
import { Plus, ScrollText } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { LegalDocumentRow } from "../../../components/legal-docs/LegalDocumentRow";
import { LegalKindPicker } from "../../../components/legal-docs/LegalKindPicker";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { EmptyState } from "../../../components/ui/EmptyState";
import { FilterChips } from "../../../components/ui/FilterChips";
import { ListError, ListSkeleton } from "../../../components/ui/ListStates";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { SelectField } from "../../../components/ui/SelectField";
import { createCertificatesGroup, listLegalDocs, type LegalDocumentListItemDto } from "../../../lib/api-legal";
import { filterLegalItems, findCertificatesGroup, LEGAL_LIST_INTRO } from "../../../lib/legal/legal-doc-view";
import { legalErrorText } from "../../../lib/legal/legal-errors";

const STATUS_CHIPS: { value: LegalListStatusFilter; label: string }[] = [
  { value: "all", label: "Все" },
  { value: "published", label: "Опубликованные" },
  { value: "draft", label: "Черновики" },
];

const KIND_OPTIONS = [{ value: "all", label: "Все типы" }, ...LEGAL_DOCUMENT_KINDS.map((k) => ({ value: k, label: LEGAL_DOCUMENT_KIND_LABELS[k] }))];

export function LegalDocumentsListPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const base = `/shop/${slug}/settings/legal-documents`;
  const [items, setItems] = useState<LegalDocumentListItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<LegalListStatusFilter>("all");
  const [kind, setKind] = useState<LegalDocumentKind | null>(null);
  const [picking, setPicking] = useState(false);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      // Весь список: отбор на клиенте — четвёртой карточке нужно знать про группу сертификатов.
      setItems((await listLegalDocs()).items);
    } catch (err) {
      setError(legalErrorText(err, "Не удалось загрузить документы"));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visible = useMemo(() => (items ? filterLegalItems(items, { status, kind }) : []), [items, status, kind]);
  const certificates = items ? findCertificatesGroup(items) : null;

  async function createCertificates() {
    setCreating(true);
    try {
      const group = await createCertificatesGroup();
      notify.success("Группа сертификатов создана и опубликована — состав собирается сам из карточек производителей");
      navigate(`${base}/${group.id}`);
    } catch (err) {
      notify.error(legalErrorText(err, "Не удалось создать группу сертификатов"));
      void refresh();
    } finally {
      setCreating(false);
    }
  }

  const newButton = (
    <Button variant="brand" onClick={() => setPicking(true)}>
      <Plus aria-hidden className="size-4" />
      Новый документ
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader title="Юридические документы" subtitle="Оферта, политика ПДн, сертификаты и другие документы" action={newButton} />
      <p className="type-body max-w-[72ch] text-on-surface-variant">{LEGAL_LIST_INTRO}</p>

      <div className="flex flex-wrap items-center gap-2">
        <FilterChips aria-label="Состояние документов" items={STATUS_CHIPS} value={status} onChange={setStatus} />
        <SelectField
          ariaLabel="Тип документа"
          value={kind ?? "all"}
          onChange={(v) => setKind(v === "all" ? null : (v as LegalDocumentKind))}
          options={KIND_OPTIONS}
          className="w-auto min-w-40"
        />
      </div>

      {error ? (
        <ListError message={error} onRetry={() => void refresh()} />
      ) : items === null ? (
        <ListSkeleton rows={3} compact />
      ) : items.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="Документов пока нет"
          description="Начните с оферты и политики ПДн — без них сайт нельзя открыть."
          action={<Button onClick={() => setPicking(true)}>Создать документ</Button>}
        />
      ) : visible.length === 0 ? (
        <EmptyState icon={ScrollText} variant="filtered" title="Под отбор ничего не подходит" description="Измените отбор по состоянию или типу." />
      ) : (
        <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {visible.map((item) => (
            <li key={item.id} className="min-w-0">
              <LegalDocumentRow item={item} to={`${base}/${item.id}`} />
            </li>
          ))}
        </ul>
      )}

      <LegalKindPicker
        open={picking}
        onClose={() => setPicking(false)}
        onPick={(k) => navigate(`${base}/new?kind=${k}`)}
        onPickCertificates={() => void createCertificates()}
        certificatesHref={certificates ? `${base}/${certificates.id}` : null}
        busy={creating}
      />
    </div>
  );
}
